import * as React from "react";
import { usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldNameMutation } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, useToast } from "randmarcomps";
import InlineSpinner from "../util/InlineSpinner";

interface EditOpportunityWrapperProps {
  bidNumber: string;
  fieldName: string;
  label: string;
  currentValue?: string;
  inputType?: React.ComponentProps<typeof Input>["type"];
  onUpdated?: () => void;
  children: React.ReactNode;
}

const toDateInputValue = (value?: string) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (/^\\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  if (/^\\d{4}-\\d{2}-\\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  return raw;
};

const EditOpportunityWrapper: React.FC<EditOpportunityWrapperProps> = ({
  bidNumber,
  fieldName,
  label,
  currentValue,
  inputType,
  onUpdated,
  children,
}) => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(currentValue || "");

  const [editOpportunity, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldNameMutation();

  const handleOpen = React.useCallback(() => {
    setValue(inputType === "date" ? toDateInputValue(currentValue) : (currentValue || ""));
    setOpen(true);
  }, [currentValue, inputType]);

  const handleConfirm = React.useCallback(async () => {
    try {
      await editOpportunity({
        routeManufacturerId: applicationId,
        bidNumber,
        fieldName,
        newValue: value,
      }).unwrap();

      toast({ variant: "success", title: "Updated", description: `${label} saved.` });
      setOpen(false);
      onUpdated?.();
    } catch (error) {
      console.error("Failed to update opportunity field:", error);
      toast({ variant: "destructive", title: "Error", description: `Could not update ${label}.` });
    }
  }, [applicationId, bidNumber, editOpportunity, fieldName, label, onUpdated, toast, value]);

  return (
    <>
      <div className="contents" onClick={handleOpen}>
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isLoading && setOpen(false)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit {label}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-4">
            <Label htmlFor={`edit-opportunity-${fieldName}`} className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </Label>
            <Input
              id={`edit-opportunity-${fieldName}`}
              autoFocus
              value={value}
              onChange={(event) => setValue(event.target.value)}
              type={inputType || "text"}
              disabled={isLoading}
              className="h-11 rounded-xl"
            />
          </div>
          <DialogFooter className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="button" className="rounded-xl" onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditOpportunityWrapper;
